/**
 * SlideLibrary
 *
 * A responsive grid of SlideCard components with infinite scroll.
 * Loads 20 slides at a time and fetches more when the user scrolls
 * near the bottom.
 *
 * When `groupByDeck` is true, slides are grouped under section headers
 * showing folder name and presentation filename.
 *
 * Grid is responsive:
 *   1 col (mobile) → 2 → 3 → 4 → 5 (wide desktop)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { SlideSenseFolder, SlideSenseSlide } from "@/features/slide-sense/types"

import { ChevronDown, Folder, Inbox, Layers, Presentation, SearchX } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

import { SlideCard } from "./SlideCard"

type SlideWithReason = SlideSenseSlide & { ai_search_reason?: string }

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SlideLibraryProps {
  slides: SlideWithReason[]
  isLoading: boolean
  isFetchingNextPage?: boolean
  hasNextPage?: boolean
  onFetchNextPage?: () => void
  onPreview?: (slide: SlideSenseSlide) => void
  /** Callback to add a slide directly to the builder. */
  onAdd?: (slide: SlideSenseSlide) => void
  /**
   * Callback to add every slide in a deck (group) to the builder.
   * Only used when `groupByDeck` is true; surfaces an "Add entire deck"
   * action on each group header.
   */
  onAddDeck?: (slides: SlideSenseSlide[]) => void
  /** When true, group slides by deck with section headers. */
  groupByDeck?: boolean
  /** Folder list for resolving folder names in group headers. */
  folders?: SlideSenseFolder[]
  /**
   * When true, an empty result set is treated as "search found nothing"
   * (shows the no-results message) rather than the generic "no slides yet".
   */
  searchActive?: boolean
}

// ---------------------------------------------------------------------------
// Skeleton placeholder shown while loading
// ---------------------------------------------------------------------------

function SlideCardSkeleton() {
  return (
    <div className="border-border/45 overflow-hidden rounded-lg border">
      <Skeleton className="aspect-[16/9] w-full" />
      <div className="space-y-2 p-2.5">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  )
}

// Number of placeholder cards rendered while a next page is loading. Matches
// the page size of the infinite query so the grid height grows once
// (skeletons → real cards) instead of twice (nothing → real cards).
const NEXT_PAGE_SKELETON_COUNT = 10

const GRID_CLASSES =
  "xs:grid-cols-2 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"

// Walk up from `el` to find the nearest scrollable ancestor. Used so the
// IntersectionObserver observes the actual scroll container the sentinel
// lives in, not the viewport — without this the sentinel can sit "in
// viewport" indefinitely and trigger back-to-back fetches.
function getScrollableAncestor(el: HTMLElement | null): HTMLElement | null {
  let parent = el?.parentElement ?? null
  while (parent) {
    const { overflowY } = getComputedStyle(parent)
    if (overflowY === "auto" || overflowY === "scroll") return parent
    parent = parent.parentElement
  }
  return null
}

// ---------------------------------------------------------------------------
// Grouping helper
// ---------------------------------------------------------------------------

interface SlideGroup {
  key: string
  folderName: string | null
  deckName: string
  slides: SlideWithReason[]
}

export function groupSlidesByDeck(
  slides: SlideWithReason[],
  folderMap: Map<string, string>
): SlideGroup[] {
  const groups = new Map<string, SlideGroup>()

  for (const slide of slides) {
    // Group by source_filename (deck), keeping folder context
    const key = `${slide.folder_id}:${slide.source_filename}`

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        folderName: folderMap.get(slide.folder_id) ?? null,
        deckName: slide.source_filename,
        slides: [],
      })
    }
    groups.get(key)!.slides.push(slide)
  }

  // Sort slides within each group by slide_index
  for (const group of groups.values()) {
    group.slides.sort((a, b) => a.slide_index - b.slide_index)
  }

  return Array.from(groups.values())
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SlideLibrary({
  slides,
  isLoading,
  isFetchingNextPage = false,
  hasNextPage = false,
  onFetchNextPage,
  onPreview,
  onAdd,
  onAddDeck,
  groupByDeck = false,
  folders = [],
  searchActive = false,
}: SlideLibraryProps) {
  const { t } = useTranslation()
  // Infinite scroll: observe a sentinel element at the bottom of the grid
  const sentinelRef = useRef<HTMLDivElement>(null)

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage && onFetchNextPage) {
        onFetchNextPage()
      }
    },
    [hasNextPage, isFetchingNextPage, onFetchNextPage]
  )

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(handleIntersect, {
      root: getScrollableAncestor(sentinel),
      rootMargin: "200px", // Start loading 200px before reaching bottom
    })
    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [handleIntersect])

  // Build folder name lookup
  const folderMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const f of folders) map.set(f.id, f.name)
    return map
  }, [folders])

  // Group slides by deck when enabled
  const groups = useMemo(
    () => (groupByDeck ? groupSlidesByDeck(slides, folderMap) : null),
    [groupByDeck, slides, folderMap]
  )

  // Collapsed-deck state (session-only). Keyed by group.key (folder_id + filename).
  // Groups start collapsed by default. We track which keys have been seen so
  // newly-arrived groups (from infinite scroll / search) also start collapsed
  // without forcing user-expanded groups back to closed.
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set())
  const seenKeysRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!groups) return
    let changed = false
    const additions = new Set<string>()
    for (const g of groups) {
      if (!seenKeysRef.current.has(g.key)) {
        seenKeysRef.current.add(g.key)
        additions.add(g.key)
        changed = true
      }
    }
    if (changed) {
      setCollapsedKeys((prev) => {
        const next = new Set(prev)
        for (const k of additions) next.add(k)
        return next
      })
    }
  }, [groups])
  const toggleCollapsed = useCallback((key: string) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  // Loading state — show skeleton grid
  if (isLoading && slides.length === 0) {
    return (
      <div className={GRID_CLASSES}>
        {Array.from({ length: 8 }).map((_, i) => (
          <SlideCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  // Empty state — distinguish "search found nothing" from "no slides yet".
  if (slides.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <div className="bg-muted/60 flex h-14 w-14 items-center justify-center rounded-full">
          {searchActive ? (
            <SearchX className="text-muted-foreground h-7 w-7" />
          ) : (
            <Inbox className="text-muted-foreground h-7 w-7" />
          )}
        </div>
        <p className="text-muted-foreground text-sm">
          {searchActive ? t("slideSense.noSearchResults") : t("slideSense.noSlidesFound")}
        </p>
        {searchActive && (
          <p className="text-muted-foreground/70 text-xs">{t("slideSense.noSearchResultsHint")}</p>
        )}
      </div>
    )
  }

  // Grouped view
  if (groups) {
    return (
      <>
        {groups.map((group, idx) => {
          const isCollapsed = collapsedKeys.has(group.key)
          // group.key contains source_filename, which can include spaces and
          // other characters that are invalid in HTML id attributes. Use the
          // stable index instead — aria-controls only needs document-unique.
          const panelId = `deck-panel-${idx}`
          return (
            <Collapsible
              key={group.key}
              asChild
              open={!isCollapsed}
              onOpenChange={(open) => {
                if (open === isCollapsed) toggleCollapsed(group.key)
              }}
            >
              <section
                aria-label={group.deckName}
                className={idx === 0 ? "mb-8" : "mt-2 mb-8 border-t pt-6"}
              >
                {/* Section header — chevron toggle + deck title + breadcrumb + add-all */}
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <button
                    aria-controls={panelId}
                    aria-expanded={!isCollapsed}
                    className="hover:bg-muted/50 -ml-1 flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-0.5 text-left transition-colors"
                    type="button"
                    aria-label={t(
                      isCollapsed ? "slideSense.expandDeck" : "slideSense.collapseDeck",
                      {
                        name: group.deckName,
                      }
                    )}
                    onClick={() => toggleCollapsed(group.key)}
                  >
                    <ChevronDown
                      className={cn(
                        "text-foreground/70 h-4 w-4 shrink-0 transition-transform duration-150",
                        isCollapsed && "-rotate-90"
                      )}
                    />
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      {group.folderName && (
                        <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                          <Folder className="h-3.5 w-3.5" />
                          <span>{group.folderName}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Presentation className="text-primary h-4 w-4 shrink-0" />
                        <h2 className="text-foreground truncate text-base font-semibold tracking-tight">
                          {group.deckName}
                        </h2>
                        <span className="bg-muted text-muted-foreground rounded-md px-2 py-0.5 text-xs tabular-nums">
                          {t("slideSense.slideCount", { count: group.slides.length })}
                        </span>
                      </div>
                    </div>
                  </button>
                  {onAddDeck && (
                    <Button
                      className="shrink-0 gap-1.5"
                      size="sm"
                      variant="default"
                      onClick={() => onAddDeck(group.slides)}
                    >
                      <Layers className="h-4 w-4" />
                      {t("slideSense.addEntireDeck")}
                    </Button>
                  )}
                </div>

                {/* Slide grid for this group (animated) */}
                <CollapsibleContent>
                  <div className={GRID_CLASSES} id={panelId}>
                    {group.slides.map((slide) => (
                      <SlideCard
                        key={slide.id}
                        aiSearchReason={slide.ai_search_reason}
                        slide={slide}
                        onAdd={onAdd}
                        onPreview={onPreview}
                      />
                    ))}
                  </div>
                </CollapsibleContent>
              </section>
            </Collapsible>
          )
        })}

        {/* Sentinel */}
        <div ref={sentinelRef} className="h-1" />

        {/* Reserve grid space with skeletons while fetching, so scroll position
            stays stable when the real cards arrive. */}
        {isFetchingNextPage && (
          <div aria-label={t("slideSense.loadingMore")} className={`${GRID_CLASSES} mt-3`}>
            {Array.from({ length: NEXT_PAGE_SKELETON_COUNT }).map((_, i) => (
              <SlideCardSkeleton key={`next-skel-${i}`} />
            ))}
          </div>
        )}

        {!hasNextPage && slides.length >= 20 && (
          <p className="text-muted-foreground py-4 text-center text-xs">
            {t("slideSense.allSlidesLoaded", { count: slides.length })}
          </p>
        )}
      </>
    )
  }

  // Flat grid (ungrouped)
  return (
    <>
      <div className={GRID_CLASSES}>
        {slides.map((slide) => (
          <SlideCard
            key={slide.id}
            aiSearchReason={slide.ai_search_reason}
            slide={slide}
            onAdd={onAdd}
            onPreview={onPreview}
          />
        ))}
        {/* Reserve grid space with skeletons while fetching, so scroll position
            stays stable when the real cards arrive. */}
        {isFetchingNextPage &&
          Array.from({ length: NEXT_PAGE_SKELETON_COUNT }).map((_, i) => (
            <SlideCardSkeleton key={`next-skel-${i}`} />
          ))}
      </div>

      {/* Sentinel: triggers next page load when scrolled into view */}
      <div ref={sentinelRef} className="h-1" />

      {/* End of results */}
      {!hasNextPage && slides.length >= 20 && (
        <p className="text-muted-foreground py-4 text-center text-xs">
          {t("slideSense.allSlidesLoaded", { count: slides.length })}
        </p>
      )}
    </>
  )
}
